import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook to track user idle time and trigger auto logout
 *
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 15)
 * @param {number} warningMinutes - Minutes before timeout to show warning (default: 1)
 * @param {function} onTimeout - Callback function to execute on timeout (logout)
 * @param {function} onWarning - Callback function to execute when warning threshold is reached
 * @returns {object} - { resetTimer, showWarning, remainingSeconds }
 */
const useIdleTimeout = (timeoutMinutes = 15, warningMinutes = 1, onTimeout, onWarning) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    const warningSeconds = warningMinutes * 60;
    setRemainingSeconds(warningSeconds);

    countdownIntervalRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningMinutes]);

  const resetTimer = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    setRemainingSeconds(0);

    // Set warning timeout (show warning X minutes before logout)
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;
    console.log(`[IdleTimeout] Timer reset - Warning in ${(warningMs / 1000 / 60).toFixed(1)} minutes, Logout in ${(timeoutMinutes)} minutes`);

    warningTimeoutRef.current = setTimeout(() => {
      console.log('[IdleTimeout] Warning: User will be logged out soon');
      setShowWarning(true);
      startCountdown();
      if (onWarning) {
        onWarning();
      }
    }, warningMs);

    // Set logout timeout
    const timeoutMs = timeoutMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(() => {
      console.log('[IdleTimeout] Timeout reached - logging out user');
      clearTimers();
      if (onTimeout) {
        onTimeout();
      }
    }, timeoutMs);
  }, [timeoutMinutes, warningMinutes, onTimeout, onWarning, clearTimers, startCountdown]);

  useEffect(() => {
    console.log(`[IdleTimeout] useEffect triggered with timeoutMinutes=${timeoutMinutes}, warningMinutes=${warningMinutes}`);

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Debounce to avoid resetting timer too frequently
    let debounceTimer = null;
    const handleActivity = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        console.log('[IdleTimeout] User activity detected, resetting timer');
        resetTimer();
      }, 1000); // Reset timer at most once per second
    };

    // Initialize timer
    console.log('[IdleTimeout] Initializing timers');
    resetTimer();

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      console.log('[IdleTimeout] Cleaning up timers and event listeners');
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [resetTimer, clearTimers, timeoutMinutes, warningMinutes]);

  return {
    resetTimer,
    showWarning,
    remainingSeconds,
    dismissWarning: () => {
      setShowWarning(false);
      resetTimer();
    }
  };
};

export default useIdleTimeout;
